import { useQuery } from "@tanstack/react-query";
import { CACHE_TIMES } from "@/lib/queryConfig";
import { fetchPlatformConstants } from "./api";
import { isInventoryEnabled } from "./featureSwitches";
import {
  designRequestLimitsFromConstants,
  getUploadConfig,
  mergePlatformConstants,
  type DesignRequestLimits,
  type PlatformConstantsMap,
  type UploadCatalogConfig,
} from "./defaults";

export const PLATFORM_CONSTANTS_QUERY_KEY = ["platform", "constants"] as const;

export function usePlatformConstantsQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: PLATFORM_CONSTANTS_QUERY_KEY,
    queryFn: () => fetchPlatformConstants(),
    enabled: options?.enabled ?? true,
    ...CACHE_TIMES.CONFIG,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

export function usePlatformConstantsMap(): PlatformConstantsMap {
  const { data } = usePlatformConstantsQuery();
  return mergePlatformConstants(data);
}

export function useDesignRequestLimits(): DesignRequestLimits {
  const constants = usePlatformConstantsMap();
  return designRequestLimitsFromConstants(constants);
}

export function useUploadConfigConstant(uploadKey: string): UploadCatalogConfig {
  const constants = usePlatformConstantsMap();
  return getUploadConfig(constants, uploadKey);
}

/** The `INVENTORY_ENABLED` switch (`feature.inventory_enabled`). False while the constants read is in flight or failed. */
export function useInventoryEnabled(): boolean {
  const constants = usePlatformConstantsMap();
  return isInventoryEnabled(constants);
}
